/** Designer-only viewport limits for the shared 2D floor plan editor canvas. */

import {
  DESIGNER_MAX_ZOOM,
  DESIGNER_MIN_ZOOM_FLOOR,
  DIMENSION_SCREEN_ARROW_PX,
  DIMENSION_SCREEN_FONT_PX,
  DIMENSION_SCREEN_STROKE_PX,
  DIMENSION_SCREEN_TEXT_OFFSET_PX,
  DRAWABLE_FRAME_STROKE_PX,
  INITIAL_VISIBLE_WIDTH_METERS,
  MAX_VISIBLE_WIDTH_METERS,
  MIN_VISIBLE_WIDTH_METERS,
  SNAP_GUIDE_SCREEN_DASH_PX,
  SNAP_GUIDE_SCREEN_GAP_PX,
  SNAP_GUIDE_SCREEN_STROKE_PX,
  ZOOM_FACTOR_2D,
} from './floorPlanDesignerConfig.js';
import { computeDisplayScale, imageToStagePoint, stageToImagePoint } from './floorPlanKonvaViewport.js';

export const DESIGNER_MAX_USER_ZOOM_FLOOR = DESIGNER_MAX_ZOOM;
export const DESIGNER_ZOOM_STEP = ZOOM_FACTOR_2D;

/** Stable on-screen hit target for wall selection/drag (invisible hit line only). */
export const DESIGNER_FIXED_SCREEN_HIT_PX = 18;
export const MIN_WALL_HIT_WIDTH_PX = 12;

/**
 * @param {number} stageWidth
 * @param {number} visibleWidthMeters
 * @param {number} pixelsPerMeter
 * @returns {number}
 */
export function computeDesignerDisplayScaleForVisibleWidth(
  stageWidth,
  visibleWidthMeters,
  pixelsPerMeter,
) {
  if (!stageWidth || !visibleWidthMeters || !pixelsPerMeter) return 1;
  return stageWidth / (visibleWidthMeters * pixelsPerMeter);
}

/**
 * @param {number} stageWidth
 * @param {number} pixelsPerMeter
 * @param {number} fitScale
 * @returns {number}
 */
export function computeDesignerMinUserZoom(stageWidth, pixelsPerMeter, fitScale) {
  if (!stageWidth || !pixelsPerMeter || !fitScale) return DESIGNER_MIN_ZOOM_FLOOR;
  const dynamicMin = stageWidth / (MAX_VISIBLE_WIDTH_METERS * pixelsPerMeter * fitScale);
  return Math.max(dynamicMin, DESIGNER_MIN_ZOOM_FLOOR);
}

/**
 * @param {number} stageWidth
 * @param {number} pixelsPerMeter
 * @param {number} fitScale
 * @returns {number}
 */
export function computeDesignerMaxUserZoom(stageWidth, pixelsPerMeter, fitScale) {
  if (!stageWidth || !pixelsPerMeter || !fitScale) return DESIGNER_MAX_USER_ZOOM_FLOOR;
  const dynamicMax = stageWidth / (MIN_VISIBLE_WIDTH_METERS * pixelsPerMeter * fitScale);
  return Math.max(dynamicMax, DESIGNER_MAX_USER_ZOOM_FLOOR);
}

/**
 * @param {number} stageWidth
 * @param {number} pixelsPerMeter
 * @param {number} fitScale
 * @returns {number}
 */
export function computeDesignerInitialUserZoom(stageWidth, pixelsPerMeter, fitScale) {
  if (!fitScale) return 1;
  const targetDisplayScale = computeDesignerDisplayScaleForVisibleWidth(
    stageWidth,
    INITIAL_VISIBLE_WIDTH_METERS,
    pixelsPerMeter,
  );
  const minZoom = computeDesignerMinUserZoom(stageWidth, pixelsPerMeter, fitScale);
  const maxZoom = computeDesignerMaxUserZoom(stageWidth, pixelsPerMeter, fitScale);
  const rawZoom = targetDisplayScale / fitScale;
  return Math.max(minZoom, Math.min(maxZoom, rawZoom));
}

/**
 * @param {{
 *   stageWidth: number;
 *   stageHeight: number;
 *   canvasW: number;
 *   canvasH: number;
 *   offsetX: number;
 *   offsetY: number;
 *   displayScale: number;
 * }} params
 * @returns {{ panX: number; panY: number }}
 */
export function computeDesignerPanForDrawableCenter({
  stageWidth,
  stageHeight,
  canvasW,
  canvasH,
  offsetX,
  offsetY,
  displayScale,
}) {
  return {
    panX: stageWidth / 2 - offsetX - (canvasW / 2) * displayScale,
    panY: stageHeight / 2 - offsetY - (canvasH / 2) * displayScale,
  };
}

/**
 * @param {number} zoom
 * @param {{ stageWidth: number; pixelsPerMeter: number; fitScale: number }} ctx
 * @returns {number}
 */
export function clampDesignerUserZoom(zoom, ctx) {
  const minZoom = computeDesignerMinUserZoom(ctx.stageWidth, ctx.pixelsPerMeter, ctx.fitScale);
  const maxZoom = computeDesignerMaxUserZoom(ctx.stageWidth, ctx.pixelsPerMeter, ctx.fitScale);
  return Math.max(minZoom, Math.min(maxZoom, zoom));
}

/**
 * @param {{
 *   stageWidth: number;
 *   stageHeight: number;
 *   canvasW: number;
 *   canvasH: number;
 *   pixelsPerMeter: number;
 *   fitScale: number;
 *   offsetX: number;
 *   offsetY: number;
 * }} params
 * @returns {{ userZoom: number; panX: number; panY: number; displayScale: number }}
 */
export function applyDesignerDefaultViewport({
  stageWidth,
  stageHeight,
  canvasW,
  canvasH,
  pixelsPerMeter,
  fitScale,
  offsetX,
  offsetY,
}) {
  const userZoom = computeDesignerInitialUserZoom(stageWidth, pixelsPerMeter, fitScale);
  const displayScale = computeDisplayScale(fitScale, userZoom);
  const { panX, panY } = computeDesignerPanForDrawableCenter({
    stageWidth,
    stageHeight,
    canvasW,
    canvasH,
    offsetX,
    offsetY,
    displayScale,
  });
  return { userZoom, panX, panY, displayScale };
}

/**
 * @param {number} displayScale
 * @returns {{
 *   dimensionStrokeWidth: number;
 *   dimensionPointerLength: number;
 *   dimensionPointerWidth: number;
 *   dimensionFontSize: number;
 *   dimensionTextOffset: number;
 *   snapGuideStrokeWidth: number;
 *   snapGuideDash: [number, number];
 *   frameStrokeWidth: number;
 * }}
 */
export function computeDesignerScreenConstants(displayScale) {
  const safeScale = Math.max(displayScale || 1, 0.0001);
  return {
    dimensionStrokeWidth: DIMENSION_SCREEN_STROKE_PX / safeScale,
    dimensionPointerLength: DIMENSION_SCREEN_ARROW_PX / safeScale,
    dimensionPointerWidth: DIMENSION_SCREEN_ARROW_PX / safeScale,
    dimensionFontSize: DIMENSION_SCREEN_FONT_PX / safeScale,
    dimensionTextOffset: DIMENSION_SCREEN_TEXT_OFFSET_PX / safeScale,
    snapGuideStrokeWidth: SNAP_GUIDE_SCREEN_STROKE_PX / safeScale,
    snapGuideDash: [SNAP_GUIDE_SCREEN_DASH_PX / safeScale, SNAP_GUIDE_SCREEN_GAP_PX / safeScale],
    frameStrokeWidth: DRAWABLE_FRAME_STROKE_PX / safeScale,
  };
}

/**
 * Stage/screen pointer → world/image coordinates (inside scaled Group).
 * @param {{ x: number; y: number }} stagePointer
 * @param {{ offsetX: number; offsetY: number; displayScale: number; panX?: number; panY?: number }} viewportTransform
 * @returns {[number, number]}
 */
export function pointerToWorld(stagePointer, viewportTransform) {
  return stageToImagePoint(stagePointer, viewportTransform);
}

/**
 * World/image coordinates → stage/screen overlay position.
 * @param {[number, number]} worldPoint
 * @param {{ offsetX: number; offsetY: number; displayScale: number; panX?: number; panY?: number }} viewportTransform
 * @returns {{ x: number; y: number }}
 */
export function worldToOverlay(worldPoint, viewportTransform) {
  return imageToStagePoint(worldPoint, viewportTransform);
}

/**
 * @param {string} label
 * @param {...unknown} args
 */
export function designerDebug(label, ...args) {
  if (process.env.NODE_ENV === 'development') {
    console.debug(`[2D Designer] ${label}`, ...args);
  }
}
